// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import { Modal, Button, Input } from "antd";

// Local Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import CustomTable from "../../components/custom-table/custom-table";
import CustomSearch from "../../components/custom-search/custom-search";
import QueriesService, { QueryItem, QueryDetailsResponse } from "./queries.service";
import { useDispatch } from "react-redux";
import { CREATE_BREADCRUMB } from "../../store/actions";

const { TextArea } = Input;

let searchTimer: any = null;

const Queries: React.FC = (): ReactElement => {
  const queriesService = new QueriesService();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const loadingState = useSelector((state: any) => state?.loading);

  const [queriesList, setQueriesList] = useState<QueryItem[]>([]);
  const [responseData, setResponseData] = useState<any>({});
  const [searchWord, setSearchWord] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Modal states
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isReplyModalVisible, setIsReplyModalVisible] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<QueryDetailsResponse | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [loadingReply, setLoadingReply] = useState(false);

  useEffect(() => {
    dispatch({
      type: CREATE_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: "Queries",
            type: "link",
            link: "/queries",
          },
        ],
      },
    });
  }, [dispatch]);

  useEffect(() => {
    fetchQueriesListData();
  }, [searchWord, statusFilter]);

  const columns: ColumnsType<QueryItem> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      fixed: "left",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 130,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (text) => <span className="truncate max-w-xs">{text}</span>,
    },
    {
      title: "Last Message",
      dataIndex: "lastMessage",
      key: "lastMessage",
      render: (lastMessage) => (
        <span className="truncate max-w-xs" title={lastMessage?.message}>
          {lastMessage?.message?.length > 50 ? `${lastMessage.message.substring(0, 50)}...` : lastMessage?.message}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        {
          text: "Open",
          value: "open",
        },
        {
          text: "Pending",
          value: "pending",
        },
        {
          text: "Closed",
          value: "closed",
        },
      ],
      filteredValue: statusFilter ? [statusFilter] : null,
      render: (status) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            status === "open"
              ? "bg-orange-500 text-white"
              : status === "pending"
              ? "bg-blue-600 text-white"
              : status === "closed"
              ? "bg-green-600 text-white"
              : "bg-gray-700 text-white"
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (
        <span className="date text-gray-500">
          {moment(text).format("MMM DD YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex gap-2">
          <ReactSVG
            src={"/images/view.svg"}
            className="cursor-pointer"
            onClick={() => handleViewQuery(record.id)}
            title="View Details"
          />
          {user?.permissions?.includes("reply_client_queries") && <ReactSVG
            src={"/images/edit.svg"}
            className="cursor-pointer"
            onClick={() => handleReplyQuery(record.id)}
            title="Reply"
          />}
        </div>
      ),
    },
  ];

  const fetchQueriesListData = async (page = 1): Promise<void> => {
    const data = await queriesService.getQueriesList(
      page,
      10,
      searchWord || undefined,
      undefined,
      undefined,
      statusFilter || undefined
    );

    if (data !== undefined) {
      setResponseData(data.pagination);
      setQueriesList(data.tickets);
    }
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    // Handle status filter changes - take the last selected value
    if (filters.status && filters.status.length > 0) {
      setStatusFilter(filters.status[filters.status.length - 1]);
    } else {
      setStatusFilter(null);
    }
  };


  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  const handleViewQuery = async (queryId: number) => {
    const queryDetails = await queriesService.getQueryDetails(queryId);
    if (queryDetails) {
      setSelectedQuery(queryDetails);
      setIsViewModalVisible(true);
    }
  };

  const handleReplyQuery = async (queryId: number) => {
    const queryDetails = await queriesService.getQueryDetails(queryId);
    if (queryDetails) {
      setSelectedQuery(queryDetails);
      setIsReplyModalVisible(true);
      setReplyMessage("");
    }
  };

  const handleSendReply = async () => {
    if (!selectedQuery || !replyMessage.trim()) {
      return;
    }

    setLoadingReply(true);
    const success = await queriesService.replyToQuery(selectedQuery.id, {
      message: replyMessage.trim(),
    });

    if (success) {
      setIsReplyModalVisible(false);
      setReplyMessage("");
      setSelectedQuery(null);
      fetchQueriesListData(); // Refresh the list
    }
    setLoadingReply(false);
  };

  const handleCloseModals = () => {
    setIsViewModalVisible(false);
    setIsReplyModalVisible(false);
    setSelectedQuery(null);
    setReplyMessage("");
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between flex-wrap">
        <BreadcrumbComponent />
      </div>

      <div className="mt-5 border-t-[1px] border-gray-400 rounded-lg">
        <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
          <p className="text-black-300 text-base font-bold">
            {!loadingState ? queriesList?.length : ""} Queries
          </p>
          <div className="flex items-center">
            <CustomSearch onChange={(event) => onSearch(event)} />
          </div>
        </div>

        {queriesList?.length ? (
          <CustomTable
            columns={columns}
            data={queriesList}
            showPagination={true}
            pageSize={responseData?.pageSize}
            totalCount={responseData?.totalCount}
            page={responseData?.currentPage}
            handleTableChange={handleTableChange}
            onPageChange={(page) => {
              fetchQueriesListData(page);
            }}
          />
        ) : !loadingState ? (
          <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
            <img
              className="w-[40px] h-[40px]"
              src="/images/no-items.svg"
              alt="no data kamco"
            />
            <p className="text-lg text-gray-500 font-medium">
              There are no queries in this list
            </p>
          </div>
        ) : (
          ""
        )}
      </div>

      {/* View Query Modal */}
      <Modal
        title="Query Details"
        open={isViewModalVisible}
        onCancel={handleCloseModals}
        footer={null}
        width={800}
      >
        {selectedQuery && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Name:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.name}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Email:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.email}</span>
              </div>
              {selectedQuery.phoneNumber && (
                <div className="flex flex-col">
                  <p className="text-black-300 text-xs font-normal mb-1">Phone:</p>
                  <span className="text-base font-semibold text-lightGray-200">{selectedQuery.phoneNumber}</span>
                </div>
              )}
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Subject:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.subject}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Callback Request:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.isCallbackRequest ? "Yes" : "No"}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Status:</p>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedQuery.status === "open"
                      ? "bg-orange-500 text-white"
                      : selectedQuery.status === "pending"
                      ? "bg-blue-600 text-white"
                      : selectedQuery.status === "closed"
                      ? "bg-green-600 text-white"
                      : "bg-gray-700 text-white"
                  }`}
                >
                  {selectedQuery.status}
                </span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Created:</p>
                <span className="text-base font-semibold text-lightGray-200">{moment(selectedQuery.createdAt).format("MMM DD YYYY hh:mm A")}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Last Replied:</p>
                <span className="text-base font-semibold text-lightGray-200">{moment(selectedQuery.lastRepliedAt).format("MMM DD YYYY hh:mm A")}</span>
              </div>
            </div>

            {selectedQuery.messages && selectedQuery.messages.length > 0 && (
              <div className="mt-5">
                <p className="text-black-300 text-xs font-normal mb-2">Messages:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedQuery.messages.map((message) => (
                    <div key={message.id} className={`p-3 rounded border ${
                      message.senderType === "admin" ? "bg-blue-50" : "bg-gray-50"
                    }`}>
                      <div className="text-black-300 text-xs font-normal mb-1">
                        {message.senderName} ({message.senderType}) - {moment(message.createdAt).format("MMM DD YYYY hh:mm A")}
                      </div>
                      <div className="text-base text-lightGray-200">{message.message}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={handleCloseModals}
                className="rounded-lg bg-white px-8 py-3 text-center outline-0 cursor-pointer text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        title="Reply to Query"
        open={isReplyModalVisible}
        onCancel={handleCloseModals}
        footer={null}
        width={600}
      >
        {selectedQuery && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Subject:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.subject}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Name:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.name}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Email:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.email}</span>
              </div>
            </div>
            <div className="flex flex-col mt-5">
              <p className="text-black-300 text-xs font-normal mb-2">Your Reply:</p>
              <TextArea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Enter your reply here..."
                rows={6}
              />
            </div>

            <div className="flex items-center justify-between mt-6">
              <button
                onClick={handleCloseModals}
                className="w-[48%] border-[1px] border-blue-500 text-blue-500 rounded-lg py-3 font-medium text-base"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSendReply}
                disabled={!replyMessage.trim() || loadingReply}
                className="disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base w-[48%] border-[1px] border-blue-500 text-white bg-blue-500 rounded-lg py-3"
              >
                {loadingReply ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Queries;